import { createTicketMessages } from 'messages/create-ticket.messages';
import {
  Button,
  Checkbox,
  CheckboxProps,
  Divider,
  Form,
  FormInstance,
  Image,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
  UploadFile
} from 'antd';
import { DatePicker, FormItem, message, Upload } from 'components/common';
import { validateMessages } from 'messages';
import { ServerUploadFile } from 'components/common/upload/ServerUpload';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  useCreateFinishedTicketMutation,
  useCreateTicketAndTransferToOrgUnitMutation,
  useCreateTicketAndTransferToOrgUnitWithoutContactMutation,
  useCreateTicketAndTransferToSupervisorMutation,
  useGetTicketDetailQuery,
  useLazyGetWrongCompletedTicketListQuery,
  usePatchConfirmMultipleRequestMutation
} from 'services/tickets';
import { ConfirmMultipleRequestTickets, CreateTicketDto } from 'types/dto/ticket';
import { useOptions, useOrganizationUnitsPagingWithoutUserOrganizationUnit, useProfile, useUploadForm } from 'hooks';
import { SelectChannels } from 'components/channels';
import { DefaultOptionType } from 'antd/es/select';
import { useCustomerDetail } from 'hooks/customers/useCustomerDetailed';
import {
  useAdministrativeUnitDetailed,
  useAdministrativeUnitDetailedByCode,
  useDistrictsPaging,
  useWardsByDistrictId
} from 'hooks/administrative-units';
import { CORE_OPTION_TYPES, DATE_FORMAT, FilesBucketNames, LOCAL_STORAGE_KEY, MICROSERVICES, PERMISSION } from 'utils';
import { useTicketSampleActive } from 'hooks/ticket-samples/useTicketSampleActive';
import dayjs from 'dayjs';
import 'dayjs/locale/vi'; // Import ngôn ngữ tiếng Việt cho dayjs
import locale from 'antd/es/date-picker/locale/vi_VN'; // Import locale tiếng Việt của Ant Design
import { useGetServiceTypeDetailQuery } from 'services';
import { ServiceTypeFormDto } from 'types';
import { createTicketValidationRules } from 'utils/validation-rules/create-ticket.validation-rules';
import { FileOutlined } from '@ant-design/icons';
import { AttentionRedCircleIcon, CalendarRedIcon, CloseIcon, NoticeCircleIcon } from 'assets';
import logoEVN from '../../assets/images/logo.png';
import { useChannelsPaging } from 'hooks/channels';
import TicketDetailed from 'components/ticket-detailed/TicketDetailed';
import { useTicketDetail } from 'hooks/tickets';
import { useAppointmentDate } from 'hooks/tickets/useGetAppointmentDate';
import { customerLookupMessages } from 'messages/customer-lookup.message';
import { checkPermissions } from 'utils/check-permission';
import RequestDetail from '../request-form/RequestDetail';

// Add CustomerSupportSession interface if not already imported
interface CustomerSupportSession {
  chatSessionId?: number;
  searchData?: any[];
  callId?: string;
  type?: 'call' | 'chat';
  searchCriteria?: { [key: string]: any };
}

export type CreateTicketFormProps = {
  onChangeLoading?: (value: boolean) => void;
  onCreateSuccess?: () => void;
  ticketId?: number;
  customerId: number;
  refetchOnMountOrArgChange?: boolean;
  serviceTypeId?: number;
  onCloseModal?: () => void;
  currentSessionId?: string | number;
  sessionType?: 'call' | 'chat';
  tabKey?: string; // The key of the current tab, used for scoping modal deletion
  initialFormData?: any; // Thêm prop này để nhận dữ liệu searchCriteria
};

export type CreateTicketFormRefProps = {
  form: FormInstance<CreateTicketFormType>;
  isLoading: boolean;
  getFieldsValue: () => any;
  setFieldsValue: (values: any) => void;
};

export type CreateTicketFormType = Omit<CreateTicketDto, 'channelId' | 'serviceTypeId' | 'uploadFileIds'> & {
  channelId: DefaultOptionType;
  serviceTypeId: DefaultOptionType;
  uploadFileIds: ServerUploadFile[];
  district?: number;
  ward?: DefaultOptionType;
  wardLabel?: string;
  districtLabel?: string;
  channelLabel?: string;
  incidentDistrict?: number;
  incidentWard?: DefaultOptionType;
};

const CreateTicketForm = forwardRef<CreateTicketFormRefProps, CreateTicketFormProps>(
  (
    {
      onChangeLoading,
      onCreateSuccess,
      ticketId,
      customerId,
      serviceTypeId,
      refetchOnMountOrArgChange = true,
      onCloseModal,
      currentSessionId,
      sessionType,
      tabKey,
      initialFormData // nhận prop này
    },
    ref
  ) => {
    const { userPermissionIds } = useProfile();
    const [form] = Form.useForm<CreateTicketFormType>();
    const previousInitialData = useRef<any>(null);
    const previousServiceTypeId = useRef<number | undefined>(undefined);
    const [contactEventId, setContactEventId] = useState<number | null>(null);
    const [defaultChannelId, setDefaultChannelId] = useState<number | null>(null);
    const isAllowCreatingTicketWithoutContact = checkPermissions(
      userPermissionIds,
      PERMISSION.CREATE_TICKET_WITHOUT_CONTACT
    );
    useImperativeHandle(ref, () => ({
      form,
      isLoading: isLoadingCreate || isLoadingCreateWithoutContact || isLoadingSupervisor,
      getFieldsValue: () => form.getFieldsValue(),
      setFieldsValue: (values) => form.setFieldsValue(values)
    }));
    const [formFields, setFormFields] = useState<ServiceTypeFormDto | null>(null);
    const { handleMultiUpload } = useUploadForm();
    const { customer } = useCustomerDetail(customerId);
    const { districts } = useDistrictsPaging({});
    const [districtId, setDistrictId] = useState(90000);
    const { wards } = useWardsByDistrictId(districtId || initialFormData?.districtId);
    const { administrativeUnitByCode } = useAdministrativeUnitDetailedByCode(customer?.administrativeUnitCode!, {
      skip: !customer?.administrativeUnitCode,
      refetchOnMountOrArgChange: true
    });
    const { administrativeUnit: initalDataAdministrativeUnit } = useAdministrativeUnitDetailed(
      initialFormData?.wardId!
    );
    const { data: serviceType, isLoading: isLoadingServiceTypeDetail } = useGetServiceTypeDetailQuery(serviceTypeId!, {
      skip: !serviceTypeId,
      refetchOnMountOrArgChange: true
    });
    const { organizationsUnits, isLoading } = useOrganizationUnitsPagingWithoutUserOrganizationUnit({
      organizationUnitClassifyId: [7]
    });
    const [selectedOrgUnitName, setSelectedOrgUnitName] = useState<string>('');

    function removeVietnameseTones(str: string): string {
      return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // remove diacritics
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D');
    }

    // Wrong Completed Ticket Check List
    const [trigger, { data: wrongTicketData, isFetching }] = useLazyGetWrongCompletedTicketListQuery();
    const [patchTicketMultipleRequestsAction] = usePatchConfirmMultipleRequestMutation();

    const phone = Form.useWatch('customerRequestPhoneNumber', form);
    const service = serviceTypeId;
    const code = customer?.customerCode;
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const handleCheckboxChange = (ticketId: number, checked: boolean) => {
      if (checked) setSelectedIds((prev) => [...prev, ticketId]);
      else setSelectedIds((prev) => prev.filter((id) => id !== ticketId));
    };

    const [autoCheckedIds, setAutoCheckedIds] = useState<number[]>([]);
    useEffect(() => {
      if (wrongTicketData?.data?.rows) {
        const autoIds = wrongTicketData.data.rows
          .filter((item) => item.isRequestMultipleTime)
          .map((item) => item.ticketId);

        setAutoCheckedIds(autoIds);
      }
    }, [wrongTicketData]);
    const [detailTicket, setDetailTicket] = useState<number>(0);
    const [modalDetailTickets, setModalDetailTickets] = useState<boolean>(false);
    const openDetailTicket = (id: number) => {
      setDetailTicket(id);
      setModalDetailTickets(true);
    };
    const closeDetailTicket = () => setModalDetailTickets(false);

    const [isInfoFilled, setIsInfoFilled] = useState(false);

    useEffect(() => {
      const filled = Boolean(phone && service && code);
      setIsInfoFilled(filled);

      if (filled)
        trigger({
          pageIndex: 1,
          pageSize: 100,
          customerCode: customer?.customerCode || 'khongdinhdanh',
          customerRequestPhoneNumber: phone,
          serviceTypeId: service || 1
        });
    }, [phone, service, code]);

    useEffect(() => {
      if (serviceType?.data?.form) {
        setFormFields(serviceType.data.form as ServiceTypeFormDto);
      }
    }, [serviceType]);

    // Thiết lập mặc định ID quận là 90000
    useEffect(() => {
      // Đặt giá trị districtId là 90000
      setDistrictId(90000);

      // Thiết lập giá trị district trong form
      form.setFieldsValue({ district: 90000 });

      // Nếu có initialFormData và có wardId, đặt giá trị ward trong form
      if (initialFormData?.wardId) {
        const ward = wards.find((ward) => ward.administrativeUnitId === initialFormData.wardId);
        // console.log(ward?.organizationUnitCode, 'ward nè', initialFormData.wardId);
        const getOrganizationUnit = organizationsUnits.find(
          (organizationsUnits) => organizationsUnits.code === ward?.organizationUnitCode
        );
        // console.log(getOrganizationUnit, 'getOrganizationUnit nè');
        if (getOrganizationUnit) {
          setSelectedOrgUnitName(getOrganizationUnit.name);
        }
        setTimeout(() => {
          form.setFieldsValue({ ward: initialFormData.wardId });
        }, 300); // Thêm timeout để đảm bảo danh sách phường/xã đã được tải
      }
    }, [form, initialFormData, organizationsUnits]);

    const handleSelectWard = (value: number) => {
      setDistrictId(value);
      // Reset các trường ward liên quan
      form.setFieldsValue({
        ward: undefined,
        incidentWard: undefined
      });
      // console.log(value, 'value nè');
    };

    // Handler for ward selection to find and display organization unit
    const handleWardChange = (value: number) => {
      // Find the selected ward
      const selectedWard = wards.find((ward) => ward.administrativeUnitId === value);

      if (selectedWard?.organizationUnitCode) {
        // Find the organization unit using the ward's organizationUnitCode
        const orgUnit = organizationsUnits.find((org) => org.code === selectedWard.organizationUnitCode);

        // Set the organization unit name to display
        setSelectedOrgUnitName(orgUnit?.name || '');
      } else {
        setSelectedOrgUnitName('');
      }
    };

    const { channels } = useChannelsPaging(form.getFieldValue('channelId'));
    const { data: createTicket, isLoading: isLoadingDetail } = useGetTicketDetailQuery(ticketId!, {
      skip: !ticketId,
      refetchOnMountOrArgChange: true
    });

    const { data: optionsCicType } = useOptions({
      optionTypeId: CORE_OPTION_TYPES.TICKET_CIC_TYPE,
      service: MICROSERVICES.CORE
    });

    const { data: optionsOutageReason } = useOptions({
      optionTypeId: CORE_OPTION_TYPES.OUTAGE_REASON,
      service: MICROSERVICES.CORE
    });

    // ✅ ADD: Move console.log to useEffect to ensure it executes
    // useEffect(() => {
    //     console.log(currentSessionId, 'currentSessionId hú hí 1');
    // }, [currentSessionId]);

    useEffect(() => {
      if (createTicket && ticketId) {
        form.setFieldsValue({
          ...createTicket.data,
          serviceTypeId: createTicket.data.serviceTypeId
            ? {
                label: createTicket.data.serviceType.name,
                value: createTicket.data.serviceTypeId
              }
            : undefined,
          channelId: createTicket.data.channelId
            ? {
                label: createTicket.data.channel.channelName,
                value: createTicket.data.channelId
              }
            : undefined,
          uploadFileIds: createTicket.data.files.map((o) => ({
            uid: `file-${o}`,
            fileId: o
          })),
          customerRequestCicIssueDate: createTicket.data.customerRequestCicIssueDate
            ? // ? dayjs.utc(createTicket.data.customerRequestCicIssueDate).format(DATE_FORMAT)
              dayjs(createTicket.data.customerRequestCicIssueDate).format(DATE_FORMAT)
            : undefined,
          appointmentDate: createTicket.data.appointmentDate
            ? dayjs.utc(createTicket.data.appointmentDate).format(DATE_FORMAT)
            : undefined
        });
      }
    }, [createTicket, ticketId]);

    const [isDirectCustomerContact, setIsDirectCustomerContact] = useState(false);
    const [isCustomerNotEnter, setIsCustomerNotEnter] = useState(false);
    const [onCreateAndTransferToOrg, { isLoading: isLoadingCreate }] = useCreateTicketAndTransferToOrgUnitMutation();
    const [onCreateAndTransferToOrgWithoutContact, { isLoading: isLoadingCreateWithoutContact }] =
      useCreateTicketAndTransferToOrgUnitWithoutContactMutation();
    const [onCreateAndTransferToSupervisor, { isLoading: isLoadingSupervisor }] =
      useCreateTicketAndTransferToSupervisorMutation();
    const [onCreateFinishedTicket, { isLoading: isLoadingCreateFinishedTicket }] = useCreateFinishedTicketMutation();
    const [submitType, setSubmitType] = useState<'orgUnit' | 'supervisor' | 'finish'>();
    const [isFinishSubmit, setIsFinishSubmit] = useState<boolean>(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [reviewData, setReviewData] = useState<CreateTicketFormType | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
    const { ticketDetail } = useTicketDetail(selectedTicketId || 0);

    const showModal = (ticketId: number) => {
      setIsModalOpen(true);
      setSelectedTicketId(ticketId);
    };
    const handleCancel = () => {
      setIsModalOpen(false);
      setSelectedTicketId(null);
    };

    const onCheckbox: CheckboxProps['onChange'] = (e) => {};

    const onCheckboxDirectCustomerContact: CheckboxProps['onChange'] = (e) => {
      const isChecked = e.target.checked;
      setIsDirectCustomerContact(isChecked);
      if (isChecked && customer) {
        const currentDialingEvent = JSON.parse(localStorage.getItem('currentCallDialingEvent') || '{}');
        //console.log(initialFormData?.customerPhoneNumber, currentDialingEvent?.customerNumber, customer.customerPhoneNumber)
        form.setFieldsValue({
          customerRequestName: customer.customerName,
          customerRequestPhoneNumber:
            initialFormData?.customerPhoneNumber || currentDialingEvent?.customerNumber || customer.customerPhoneNumber,
          customerRequestAddress: customer.customerAddress,
          district: administrativeUnitByCode?.parentId || 90000, // Gán ID quận/huyện
          incidentAddress: customer.customerAddress,
          ward: administrativeUnitByCode
            ? {
                label: administrativeUnitByCode.administrativeUnitName,
                value: administrativeUnitByCode.administrativeUnitId
              }
            : undefined, // Gán ID phường/xã
          incidentDistrict: 90000,
          incidentWard: administrativeUnitByCode
            ? {
                label: administrativeUnitByCode.administrativeUnitName,
                value: administrativeUnitByCode.administrativeUnitId
              }
            : undefined // Gán ID phường/xã
        });
      } else {
        form.setFieldsValue({
          customerRequestName: '',
          customerRequestPhoneNumber: '',
          customerRequestAddress: '',
          incidentAddress: '',
          district: undefined, // Reset giá trị
          ward: undefined, // Reset giá trị
          incidentDistrict: 90000,
          incidentWard: undefined
        });
      }
    };

    const onCheckboxCustomerNotEnter: CheckboxProps['onChange'] = (e) => {
      const isChecked = e.target.checked;
      setIsCustomerNotEnter(isChecked);
      if (isChecked) {
        form.setFieldsValue({
          customerRequestName: 'không cung cấp',
          customerRequestAddress: 'không cung cấp'
        });
      } else {
        form.setFieldsValue({
          customerRequestName: '',
          customerRequestAddress: ''
        });
      }
    };

    const [content, setContent] = useState('');
    const handleTicketSampleClick = (sampleContent: string) => {
      form.setFieldsValue({ content: sampleContent });
      setContent(sampleContent);
    };
    const [suggestions, setSuggestions] = useState<string[]>([]); // Danh sách gợi ý
    const [isSuggestionVisible, setIsSuggestionVisible] = useState(false); // Hiển thị gợi ý
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const { ticketSampleActive } = useTicketSampleActive({ serviceTypeId: serviceTypeId });

    const handleHotkeyInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const inputValue = e.target.value;
      setContent(inputValue);

      // Kiểm tra nếu có #hotkey trong nội dung nhập
      const match = inputValue.match(/#(\w*)$/); // Tìm từ sau dấu #
      if (match) {
        const hotkey = match[1]; // Lấy hotkey từ input
        const filteredSuggestions =
          ticketSampleActive?.filter((sample) => sample.hotkey?.startsWith(hotkey)).map((sample) => sample.hotkey) ||
          [];

        // Loại bỏ các giá trị undefined
        setSuggestions(filteredSuggestions.filter((suggestion): suggestion is string => suggestion !== undefined));
        setIsSuggestionVisible(filteredSuggestions.length > 0);
      } else {
        setIsSuggestionVisible(false);
      }
    };

    const handleSuggestionSelect = (hotkey: string) => {
      if (textareaRef.current) {
        const cursorPosition = textareaRef.current.selectionStart;
        const beforeCursor = content.slice(0, cursorPosition);
        const afterCursor = content.slice(cursorPosition);

        // Tìm nội dung gợi ý tương ứng với hotkey
        const suggestionContent = ticketSampleActive?.find((sample) => sample.hotkey === hotkey)?.content || hotkey;

        // Thay thế hotkey (bao gồm cả dấu #) bằng nội dung gợi ý
        const updatedContent = beforeCursor.replace(/#\w*$/, suggestionContent) + afterCursor;

        setContent(updatedContent.trim()); // Loại bỏ khoảng trắng thừa nếu có
        setIsSuggestionVisible(false);
        textareaRef.current.focus();
      }
    };
    useEffect(() => {
      form.setFieldsValue({ content }); // Đồng bộ giá trị state với Form
    }, [content, form]);

    const { data: appointmentDateData, isLoading: isAppointmentDateLoading } = useAppointmentDate('');
    const [defAppointmentDate, setDefAppointmentDate] = useState(dayjs(appointmentDateData?.data));

    useEffect(() => {
      const isUrgentService =
        serviceType?.data?.code === 'baoMatDien' ||
        serviceType?.data?.code === 'anToanDien' ||
        serviceType?.data?.code === 'baoMatDienDoNo';

      if (isUrgentService) {
        setDefAppointmentDate(dayjs());
        form.setFieldValue('appointmentDate', dayjs());
      } else {
        setDefAppointmentDate(dayjs(appointmentDateData?.data));
        form.setFieldValue('appointmentDate', dayjs(appointmentDateData?.data));
      }
    }, [serviceType?.data?.code, appointmentDateData?.data]);

    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [previewImage, setPreviewImage] = useState<string | undefined>(undefined);
    const [previewVisible, setPreviewVisible] = useState(false);

    const handlePreview = async (file: UploadFile) => {
      if (!file.url && !file.preview) {
        file.preview = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.readAsDataURL(file.originFileObj as Blob);
          reader.onload = () => resolve(reader.result as string);
        });
      }
      setPreviewImage(file.url || file.preview);
      setPreviewVisible(true);
    };

    const handleChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
      setFileList(newFileList);
    };

    const renderFilePreview = (file: UploadFile) => {
      const fileType = file.type || '';
      if (fileType.startsWith('image/')) {
        // Hiển thị hình ảnh
        return (
          <img
            src={file.url || (file.preview as string)}
            alt={file.name}
            style={{ width: '100px', height: '100px', objectFit: 'cover' }}
          />
        );
      } else if (fileType === 'application/pdf') {
        // Hiển thị PDF
        return (
          <a href={file.url || (file.preview as string)} target='_blank' rel='noopener noreferrer'>
            <FileOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
            <p>{file.name}</p>
          </a>
        );
      } else {
        // Hiển thị các định dạng khác
        return (
          <div>
            <FileOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
            <p>{file.name}</p>
          </div>
        );
      }
    };

    const showReviewModal = (type: 'orgUnit' | 'supervisor' | 'finish') => {
      // Set finish submit state to conditionally validate ward field
      setIsFinishSubmit(type === 'finish');

      // Use a timeout to ensure state is updated before validation
      setTimeout(() => {
        form
          .validateFields()
          .then((values) => {
            const channelValue = typeof values.channelId === 'object' ? values.channelId?.value : values.channelId;

            const fullReviewData: CreateTicketFormType & {
              channelLabel?: string;
              districtLabel?: string;
              wardLabel?: string;
            } = {
              ...values,
              customerCode: customer?.customerCode || '',
              organizationUnitId: customer?.organizationUnit?.organizationUnitId || 0,
              administrativeUnitId: customer?.administrativeUnitCode ? Number(customer.administrativeUnitCode) : 0,
              // Thêm thông tin để hiển thị
              channelLabel: channels?.find((ch) => ch.channelId === channelValue)?.channelName,
              //channelLabel: values.channelId?.label || "N/A",
              // Ưu tiên lấy dữ liệu từ form trước, sau đó mới fallback về customer data
              districtLabel:
                districts?.find((d) => d.administrativeUnitId === values.district)?.administrativeUnitName ||
                (isDirectCustomerContact
                  ? districts?.find((d) => d.administrativeUnitId === administrativeUnitByCode?.parentId)
                      ?.administrativeUnitName
                  : undefined),
              wardLabel:
                wards?.find(
                  (w) => w.administrativeUnitId === (typeof values.ward === 'object' ? values.ward.value : values.ward)
                )?.administrativeUnitName ||
                (isDirectCustomerContact ? administrativeUnitByCode?.administrativeUnitName : undefined)
            };
            // console.log(showReviewModal, 'showReviewModal nè');
            setReviewData(fullReviewData);
            setSubmitType(type);
            setIsModalVisible(true);
          })
          .catch((error) => {
            console.error('Validation failed:', error);
            // Reset finish submit state if validation fails
            if (type === 'finish') {
              setIsFinishSubmit(false);
            }
          });
      }, 0);
    };

    const onFinish = async ({ ...values }: CreateTicketFormType) => {
      if (selectedIds.length > 0) {
        const ticketIds: ConfirmMultipleRequestTickets = { ticketIds: selectedIds };
        patchTicketMultipleRequestsAction(ticketIds)
          .unwrap()
          .then((res) => {
            // console.log(res);
          })
          .catch((err) => {
            // console.log(err)
          });
      }

      const updateFileList = values.uploadFileIds;
      const filesUploaded = await handleMultiUpload(updateFileList);
      const formattedCicIssueDate = values.customerRequestCicIssueDate
        ? dayjs(values.customerRequestCicIssueDate).format('DD/MM/YYYY')
        : undefined;
      const wardValue = typeof values.ward === 'object' ? values.ward?.value : values.ward;

      const incidentWardValue =
        typeof values.incidentWard === 'object' ? values.incidentWard?.value : values.incidentWard;
      console.log(contactEventId, isAllowCreatingTicketWithoutContact, 'jkjk');

      if (!contactEventId && !isAllowCreatingTicketWithoutContact) {
        message.error('Không thể tạo phiếu khi không có liên hệ');
        return;
      }

      let data: CreateTicketDto = {
        ...values,
        customerCode: customer?.customerCode || 'khongdinhdanh',
        organizationUnitId: customer?.organizationUnit?.organizationUnitId,
        administrativeUnitId: (wardValue as number) || (values.district as number) || 0,
        channelId: form.getFieldValue('channelId'),
        serviceTypeId: serviceTypeId,
        uploadFileIds: filesUploaded,
        appointmentDate: form.getFieldValue('appointmentDate'),
        customerRequestCicIssueDate: formattedCicIssueDate,
        incidentAdministrativeUnitId: incidentWardValue as number,
        customerRequestPhoneNumber: form.getFieldValue('customerRequestPhoneNumber')?.trim() || undefined,
        customerRequestYearOfBirth: form.getFieldValue('customerRequestYearOfBirth') || undefined,
        customerRequestCicNumber: form.getFieldValue('customerRequestCicNumber') || undefined,
        customerRequestCicIssuePlace: form.getFieldValue('customerRequestCicIssuePlace') || undefined,
        contactEventId: contactEventId || undefined
      };

      // Nếu có contactEventId thì thêm vào
      if (!isAllowCreatingTicketWithoutContact && contactEventId) {
        data.contactEventId = contactEventId;
      }

      // Helper function to remove the current session from localStorage
      const removeCurrentSessionFromStorage = (tabKeyParam?: string) => {
        // Use the passed tabKey parameter or fall back to the component's tabKey
        const effectiveTabKey = tabKeyParam || tabKey;
        if (!currentSessionId) return;

        try {
          // Read the current sessions from localStorage
          const storedSessions = localStorage.getItem('customerSupportSession');
          if (!storedSessions) return;

          // Parse the sessions
          const allSessions = JSON.parse(storedSessions);

          // Find the session that matches the current ID
          const currentSessionIndex = allSessions.findIndex(
            (session: any) =>
              (typeof currentSessionId === 'string' && session.callId === currentSessionId) ||
              (typeof currentSessionId === 'number' && session.chatSessionId === currentSessionId)
          );

          if (currentSessionIndex === -1) {
            // console.log('⚠️ Session not found for removal');
            return;
          }

          // Get the current session
          const currentSession = allSessions[currentSessionIndex];

          // Check if we have searchData with form data to modify
          if (currentSession.searchData?.length > 0) {
            // console.log('🔍 Found search data, looking for form data to remove');

            // Flag to track if we've made changes
            let formDataRemoved = false;

            // Loop through each tab to find and remove the matching form data
            currentSession.searchData = currentSession.searchData.map((tab: any) => {
              // CRITICAL FIX: Only process the current tab if tabKey is provided
              // This ensures we only remove modals from the active tab
              if (effectiveTabKey && tab.key !== effectiveTabKey) {
                return tab; // Skip tabs that don't match the current tabKey
              }

              if (!tab.formData || !tab.minimizedModals) return tab;

              // Find if this tab has the form we're looking for
              const modalIndex = tab.minimizedModals.findIndex((modalId: any) => {
                // Check by serviceTypeId in the form data
                if (tab.formData[modalId]?.serviceTypeId === serviceTypeId) {
                  return true;
                }

                // Check if the modalId itself is the serviceTypeId
                return modalId === serviceTypeId;
              });

              if (modalIndex !== -1) {
                // Found the matching form data - get its ID
                const modalId = tab.minimizedModals[modalIndex];
                // console.log(
                //   `✅ Found form data for serviceTypeId ${serviceTypeId} in modal ID ${modalId} in tab ${tab.key}`
                // );

                // Create new minimizedModals array without this modal
                const newMinimizedModals = [...tab.minimizedModals];
                newMinimizedModals.splice(modalIndex, 1);

                // Create new formData object without this modal
                const newFormData = { ...tab.formData };
                delete newFormData[modalId];

                formDataRemoved = true;

                // Return modified tab
                return {
                  ...tab,
                  minimizedModals: newMinimizedModals,
                  formData: newFormData
                };
              }

              return tab;
            });

            if (formDataRemoved) {
              // console.log(`✅ Successfully removed form data for tab ${effectiveTabKey}, preserving session`);

              // Update the session with our modified data
              allSessions[currentSessionIndex] = currentSession;

              // Save the updated sessions back to localStorage
              localStorage.setItem('customerSupportSession', JSON.stringify(allSessions));

              // Dispatch event to notify about minimized count change
              window.dispatchEvent(
                new CustomEvent('minimizedTicketsChanged', {
                  detail: { sessionId: currentSessionId, tabKey: effectiveTabKey }
                })
              );

              // 🚨 CRITICAL FIX: Dispatch event to update UI immediately
              window.dispatchEvent(
                new CustomEvent('minimizedModalsCleared', {
                  detail: { sessionId: currentSessionId, tabKey: effectiveTabKey }
                })
              );

              return; // Exit early - we're done!
            } else {
              // console.log(`⚠️ Could not find matching form data to remove in tab ${effectiveTabKey}`);
            }
          }

          // If we didn't find specific form data to remove or couldn't remove it,
          // keep the session but log a warning
          // console.log('⚠️ No specific form data found, but preserving session');
        } catch (error) {
          // console.error('❌ Error updating session data in localStorage:', error);
        }
      };

      if (submitType === 'orgUnit') {
        if (isAllowCreatingTicketWithoutContact) {
          onCreateAndTransferToOrgWithoutContact(data)
            .unwrap()
            .then((response) => {
              // if (response.statusCode === 200) {
              message.success(createTicketMessages.createTicketSuccess);
              onCreateSuccess?.();
              onCloseModal?.();

              // Use the updated function to selectively remove form data only
              // Pass tabKey to ensure we only remove modals from the current tab
              removeCurrentSessionFromStorage(tabKey);

              // This line will still clear tabs separate from session
              localStorage.removeItem(LOCAL_STORAGE_KEY.CUSTOMER_LOOKUP_DETAILED_TABS);
              // } else if (response.statusCode === 409) {
              //     //message.error(response.message || createTicketMessages.createTicketError);
              //     message.error('lỗi nè')
              // }
            })
            .catch((error) => {
              if (error.message === createTicketMessages.ticketExists) {
                // const errorMessage = (error as any)?.data?.message;
                // message.error(errorMessage);
                onCloseModal?.();
                Modal.confirm({
                  title: createTicketMessages.ticketExists,
                  content: createTicketMessages.ticketExistsContent,
                  onOk: () => {
                    setIsModalOpen(error.data.ticketId);
                    showModal(error.data.ticketId);
                  }
                });
              }
            });
        } else {
          onCreateAndTransferToOrg(data)
            .unwrap()
            .then((response) => {
              // if (response.statusCode === 200) {
              message.success(createTicketMessages.createTicketSuccess);
              onCreateSuccess?.();
              onCloseModal?.();

              // Use the updated function to selectively remove form data only
              removeCurrentSessionFromStorage(tabKey);

              // This line will still clear tabs separate from session
              localStorage.removeItem(LOCAL_STORAGE_KEY.CUSTOMER_LOOKUP_DETAILED_TABS);
              // } else if (response.statusCode === 409) {
              //     //message.error(response.message || createTicketMessages.createTicketError);
              //     message.error('lỗi nè')
              // }
            })
            .catch((error) => {
              if (error.message === createTicketMessages.ticketExists) {
                // const errorMessage = (error as any)?.data?.message;
                // message.error(errorMessage);
                onCloseModal?.();
                Modal.confirm({
                  title: createTicketMessages.ticketExists,
                  content: createTicketMessages.ticketExistsContent,
                  onOk: () => {
                    setIsModalOpen(error.data.ticketId);
                    showModal(error.data.ticketId);
                  }
                });
              }
            });
        }
      } else if (submitType === 'supervisor') {
        onCreateAndTransferToSupervisor(data)
          .unwrap()
          .then((response) => {
            message.success(createTicketMessages.createTicketSuccess);
            onCreateSuccess?.();
            onCloseModal?.();

            // Use the updated function to selectively remove form data only
            removeCurrentSessionFromStorage(tabKey);

            localStorage.removeItem(LOCAL_STORAGE_KEY.CUSTOMER_LOOKUP_DETAILED_TABS);
          })
          .catch((error) => {
            if (error.message === createTicketMessages.ticketExists) {
              // const errorMessage = (error as any)?.data?.message;
              // message.error(errorMessage);
              onCloseModal?.();
              Modal.confirm({
                title: createTicketMessages.ticketExists,
                content: createTicketMessages.ticketExistsContent,
                onOk: () => {
                  setIsModalOpen(error?.data?.ticketId);
                  showModal(error?.data?.ticketId);
                }
              });
            }
          });
      } else if (submitType === 'finish') {
        onCreateFinishedTicket(data)
          .unwrap()
          .then((response) => {
            message.success(createTicketMessages.createTicketSuccess);
            onCreateSuccess?.();
            onCloseModal?.();

            // Use the updated function to selectively remove form data only
            removeCurrentSessionFromStorage(tabKey);

            localStorage.removeItem(LOCAL_STORAGE_KEY.CUSTOMER_LOOKUP_DETAILED_TABS);
          })
          .catch((error) => {
            if (error.message === createTicketMessages.ticketExists) {
              // const errorMessage = (error as any)?.data?.message;
              // message.error(errorMessage);
              onCloseModal?.();
              Modal.confirm({
                title: createTicketMessages.ticketExists,
                content: createTicketMessages.ticketExistsContent,
                onOk: () => {
                  setIsModalOpen(error.data.ticketId);
                  showModal(error.data.ticketId);
                }
              });
            }
          });
      }
    };

    useEffect(() => {
      if (onChangeLoading) {
        onChangeLoading(isLoadingCreate || isLoadingSupervisor || isLoadingCreateWithoutContact);
      }
    }, [onChangeLoading, isLoadingCreate || isLoadingSupervisor || isLoadingCreateWithoutContact]);

    const normFile = ({ fileList }: { fileList: UploadFile[] }) => fileList;
    const defaultValues = {
      appointmentDate: defAppointmentDate,
      customerRequestName: initialFormData?.customerName,
      customerRequestPhoneNumber: initialFormData?.customerPhoneNumber,
      customerRequestAddress: initialFormData?.customerAddress,
      district: initialFormData?.districtId,
      incidenAddress: initialFormData?.customerAddress,
      ward: initalDataAdministrativeUnit
        ? {
            label: initalDataAdministrativeUnit?.administrativeUnitName,
            value: initalDataAdministrativeUnit?.administrativeUnitId
          }
        : undefined,
      incidentDistrict: 90000,
      incidentWard: initalDataAdministrativeUnit
        ? {
            label: initalDataAdministrativeUnit?.administrativeUnitName,
            value: initalDataAdministrativeUnit?.administrativeUnitId
          }
        : undefined,
      channelId: defaultChannelId // Set default channelId
    };
    // Replace the problematic effect with an improved version
    useEffect(() => {
      // Skip if data hasn't actually changed - prevents infinite loops
      if (
        JSON.stringify(previousInitialData.current) === JSON.stringify(initialFormData) &&
        previousServiceTypeId.current === serviceTypeId
      ) {
        return;
      }

      // Update our refs to track current values
      previousInitialData.current = initialFormData;
      previousServiceTypeId.current = serviceTypeId;

      // Reset the form only when serviceTypeId changes
      if (previousServiceTypeId.current !== serviceTypeId) {
        form.resetFields();
        setContent(''); // Clear content state
      }

      // Then apply initialFormData if available
      if (initialFormData && Object.keys(initialFormData).length > 0) {
        // Process dates in the form data
        const processedData = { ...initialFormData };

        // Convert date strings to dayjs objects
        if (processedData.appointmentDate && typeof processedData.appointmentDate === 'string') {
          try {
            const parsedDate = dayjs(processedData.appointmentDate);
            if (parsedDate.isValid()) {
              processedData.appointmentDate = parsedDate;
            } else {
              console.warn('Invalid date format for appointmentDate:', processedData.appointmentDate);
              processedData.appointmentDate = undefined;
            }
          } catch (error) {
            console.error('Error parsing appointmentDate:', error);
            processedData.appointmentDate = undefined;
          }
        }

        if (
          processedData.customerRequestCicIssueDate &&
          typeof processedData.customerRequestCicIssueDate === 'string'
        ) {
          try {
            // Kiểm tra xem chuỗi có phải định dạng ISO hoặc định dạng ngày hợp lệ không
            const parsedDate = dayjs(processedData.customerRequestCicIssueDate);
            if (parsedDate.isValid()) {
              processedData.customerRequestCicIssueDate = parsedDate;
            } else {
              // Nếu không hợp lệ, xóa giá trị để tránh lỗi
              console.warn(
                'Invalid date format for customerRequestCicIssueDate:',
                processedData.customerRequestCicIssueDate
              );
              processedData.customerRequestCicIssueDate = undefined;
            }
          } catch (error) {
            console.error('Error parsing customerRequestCicIssueDate:', error);
            processedData.customerRequestCicIssueDate = undefined;
          }
        }

        if (processedData.outageAt && typeof processedData.outageAt === 'string') {
          try {
            const parsedDate = dayjs(processedData.outageAt);
            if (parsedDate.isValid()) {
              processedData.outageAt = parsedDate;
            } else {
              console.warn('Invalid date format for outageAt:', processedData.outageAt);
              processedData.outageAt = undefined;
            }
          } catch (error) {
            console.error('Error parsing outageAt:', error);
            processedData.outageAt = undefined;
          }
        }

        // Apply the form data directly
        form.setFieldsValue(processedData);

        // If content is present, update the content state too
        if (processedData.content) {
          setContent(processedData.content);
        }
      }
    }, [initialFormData, serviceTypeId, form]); // Minimal dependencies
    useEffect(() => {
      if (!currentSessionId) return;

      try {
        const storedSessions = localStorage.getItem('customerSupportSession');
        if (!storedSessions) return;

        const sessions = JSON.parse(storedSessions);

        // Determine session type based on currentSessionId
        const isCallSession = typeof currentSessionId === 'string';
        // Find the current session
        const currentSession = sessions.find((s: any) =>
          isCallSession ? s.callId === currentSessionId : s.chatSessionId === currentSessionId
        );

        console.log(currentSession, 'currentSession nè');

        if (currentSession?.contactResponse?.contactEventId) {
          setContactEventId(currentSession.contactResponse.contactEventId);

          // Set default channelId if available
          if (currentSession.contactResponse.channelId) {
            setDefaultChannelId(currentSession.contactResponse.channelId);

            // Update the form with the channel ID
            setTimeout(() => {
              form.setFieldValue('channelId', currentSession.contactResponse.channelId);
            }, 0);
          }
        }
      } catch (error) {
        console.error('❌ Error extracting contactEventId from session:', error);
      }
    }, [currentSessionId, form]);
    // Create dynamic validation rules based on submission type
    const validationRules = {
      ...createTicketValidationRules,
      // Override ward validation to make it optional when finishing at center
      ward: isFinishSubmit
        ? [] // Empty array means no validation rules (optional field)
        : createTicketValidationRules.ward // Use original rules otherwise
    };
    if (isAppointmentDateLoading) return <div>Loading...</div>;
    // Compute default values including searchCriteria

    // Add after [form] = Form.useForm<CreateTicketFormType>();
    // Handle initialFormData separately from defaultValues

    return (
      <div className='bg-clip-padding] h-[830px] w-[53%] overflow-y-scroll rounded-l-[12px] rounded-r-none bg-[#F5F5F7] bg-no-repeat'>
        <Form
          className='mx-6 mt-2'
          form={form}
          layout='horizontal'
          onFinish={onFinish}
          validateMessages={validateMessages}
          initialValues={{
            ...defaultValues,
            channelId: defaultChannelId // Ensure channelId is set in initialValues
          }}
          scrollToFirstError={{ behavior: 'smooth', block: 'start' }}
        >
          {/* console.log('[CreateTicketForm] Form initialValues with channelId:', defaultValues) */}
          <Spin spinning={isLoadingCreate || isLoadingSupervisor || isLoadingCreateWithoutContact}>
            <div className='mt-4'>
              <Typography.Text className='text-left text-[16px]/[20px] font-semibold text-[#141414]'>
                {createTicketMessages.requesterInformation}
              </Typography.Text>
            </div>
            <div className='ml-[182px] flex flex-row gap-6'>
              {customerId !== 0 && (
                <FormItem>
                  <Checkbox className='mt-2' onChange={onCheckboxDirectCustomerContact}>
                    <Typography.Text className='text-left text-[16px]/[20px] tracking-normal text-[#868D97] opacity-100'>
                      {createTicketMessages.directCustomerContact}
                    </Typography.Text>
                  </Checkbox>
                </FormItem>
              )}
              <FormItem>
                <Checkbox className='mt-2' onChange={onCheckboxCustomerNotEnter}>
                  <Typography.Text className='text-left text-[16px]/[20px] tracking-normal text-[#868D97] opacity-100'>
                    {createTicketMessages.customerNotEnter}
                  </Typography.Text>
                </Checkbox>
              </FormItem>
            </div>
            {formFields?.requester && (
              <FormItem<CreateTicketFormType>
                name='customerRequestName'
                label={
                  <Typography.Text className='w-[156px] text-right text-[16px]/[22px] tracking-normal text-[#141414] opacity-100'>
                    {createTicketMessages.fullName}
                  </Typography.Text>
                }
                rules={validationRules.customerRequestName}
              >
                <Input className='h-[40px] w-[552px]' />
              </FormItem>
            )}

            {formFields?.contactPhone && (
              <FormItem<CreateTicketFormType>
                name='customerRequestPhoneNumber'
                label={
                  <Typography.Text className='ml-3 w-[156px] text-right text-[16px]/[22px] tracking-normal text-[#141414] opacity-100'>
                    {createTicketMessages.phoneNumber}
                  </Typography.Text>
                }
                rules={validationRules.customerRequestPhoneNumber}
              >
                <Input placeholder={createTicketMessages.phone} className='h-[40px] w-[272px]' />
              </FormItem>
            )}
            {formFields?.address && (
              <FormItem<CreateTicketFormType>
                name='customerRequestAddress'
                label={
                  <Typography.Text className='w-[156px] text-right text-[16px]/[22px] tracking-normal text-[#141414] opacity-100'>
                    {createTicketMessages.address}
                  </Typography.Text>
                }
                rules={validationRules.customerRequestAddress}
              >
                <Input placeholder={createTicketMessages.addressAndStreet} className='h-[40px] w-[552px]' />
              </FormItem>
            )}
            {formFields?.area && (
              <div className='flex flex-row gap-2'>
                <FormItem
                  name='district'
                  label={
                    <Typography.Text className='w-[158px] text-right text-[16px]/[22px] tracking-normal text-[#141414] opacity-100'>
                      {createTicketMessages.ward}
                    </Typography.Text>
                  }
                  rules={validationRules.district}
                  style={{ display: 'none' }}
                >
                  <Input type='hidden' />
                </FormItem>
                <FormItem
                  label={<Typography.Text>{createTicketMessages.ward}</Typography.Text>}
                  name='ward'
                  rules={validationRules.ward}
                  className='ml-[110px]'
                >
                  <Select
                    showSearch
                    optionFilterProp='children'
                    className='h-[40px] w-[272px]'
                    dropdownStyle={{ minWidth: '500px' }}
                    placeholder={createTicketMessages.selectWard}
                    options={[
                      ...(wards?.map((itm) => ({
                        label: itm.administrativeUnitName,
                        value: itm.administrativeUnitId
                      })) || [])
                    ]}
                    filterOption={(input, option) =>
                      removeVietnameseTones(option?.label ?? '')
                        .toLowerCase()
                        .includes(removeVietnameseTones(input).toLowerCase())
                    }
                    allowClear
                    onChange={handleWardChange}
                    notFoundContent={
                      <div className='flex flex-row items-center gap-2'>
                        <NoticeCircleIcon />
                        <Typography.Text type='danger' className='text-[14px]/[20px] font-normal'>
                          {customerLookupMessages.pleaseSelectDistrict}
                        </Typography.Text>
                      </div>
                    }
                  />
                </FormItem>
                {selectedOrgUnitName && (
                  <div className='mb-3 ml-2 flex items-center'>
                    <Typography.Text className='text-[16px]/[20px] text-[#141414]'>
                      Đơn vị: {selectedOrgUnitName}
                    </Typography.Text>
                  </div>
                )}
              </div>
            )}
            {formFields?.isNewAddress && (
              <FormItem name='isAddressChange' valuePropName='checked'>
                <Checkbox className='ml-[182px]' onChange={onCheckbox}>
                  <Typography.Text className='text-left text-[16px]/[20px] tracking-normal text-[#868D97] opacity-100'>
                    {createTicketMessages.newAddress}
                  </Typography.Text>
                </Checkbox>
              </FormItem>
            )}
            {formFields?.receivingChannel && (
              <FormItem<CreateTicketFormType>
                name='channelId'
                label={
                  <Typography.Text className='w-[156px] text-right text-[16px]/[22px] tracking-normal text-[#141414] opacity-100'>
                    {createTicketMessages.receptionChannel}
                  </Typography.Text>
                }
                rules={validationRules.channelId}
              >
                <SelectChannels className='h-[40px] w-[272px]' defaultValue={defaultChannelId} />
              </FormItem>
            )}
            {formFields?.cccdInformation && (
              <>
                <div className='flex flex-row gap-2'>
                  <FormItem
                    name='customerRequestCicNumber'
                    label={
                      <Typography.Text className='ml-2 w-[158px] text-right text-[16px]/[22px] tracking-normal text-[#141414] opacity-100'>
                        {createTicketMessages.citizenId}
                      </Typography.Text>
                    }
                  >
                    <Input placeholder={createTicketMessages.citizenIdNumber} className='h-[40px] w-[272px]' />
                  </FormItem>
                  <FormItem<CreateTicketFormType>
                    name='customerRequestCicIssueDate'
                    getValueProps={(value) => ({
                      value: value ? dayjs(value) : null
                    })}
                    normalize={(value) => {
                      return value ? dayjs(value) : null;
                    }}
                  >
                    <DatePicker
                      locale={locale}
                      placeholder={createTicketMessages.issueDate}
                      className='h-[40px] w-[272px]'
                      format='DD/MM/YYYY'
                      disabledDate={(current) => {
                        // Vô hiệu hóa ngày trong tương lai
                        return current && current > dayjs().endOf('day');
                      }}
                    />
                  </FormItem>
                </div>

                <div className='flex flex-row gap-2'>
                  <FormItem name='customerRequestYearOfBirth'>
                    <Input placeholder={createTicketMessages.yearOfBirth} className='ml-[182px] h-[40px] w-[272px]' />
                  </FormItem>

                  <FormItem name='customerRequestCicIssuePlace'>
                    <Input placeholder={createTicketMessages.placeOfIssue} className='h-[40px] w-[272px]' />
                  </FormItem>
                </div>

                <div className='flex flex-row gap-2'>
                  <FormItem name='customerRequestCicType'>
                    <Select
                      allowClear
                      className='ml-[182px] h-[40px] w-[272px]'
                      placeholder={createTicketMessages.contactPerson}
                      options={[
                        ...(optionsCicType.map((itm) => ({
                          label: itm.name,
                          value: itm.optionId
                        })) || [])
                      ]}
                      onChange={(value, option) => {
                        // console.log(value, option, 'value cic type')
                        // Assuming option is DefaultOptionType or similar { label: string, value: any }
                        // Or if option is not directly the {label, value} object, adjust accordingly.
                        // For Ant Design Select, the second argument 'option' can be the option object or an array of options if mode="multiple"
                        // Let's assume 'option' here is the selected option object.
                        const selectedLabel = (option as DefaultOptionType)?.label;

                        if (value === 56 && selectedLabel === 'Không cung cấp') {
                          form.setFieldsValue({
                            customerRequestCicIssuePlace: undefined,
                            customerRequestCicIssueDate: undefined,
                            customerRequestYearOfBirth: undefined,
                            customerRequestCicNumber: undefined
                          });
                        }
                      }}
                    />
                  </FormItem>
                </div>
              </>
            )}

            <Divider style={{ borderColor: '#D4D8DE', borderWidth: '1px' }} className='my-5 w-full' type='horizontal' />
            <div className='mt-4 pb-4'>
              <Typography.Text className='text-left text-[16px]/[20px] font-semibold text-[#141414]'>
                {createTicketMessages.requestServiceInfo}
              </Typography.Text>
            </div>
            {/* {formFields?.serviceType && (
                        <FormItem<CreateTicketFormType>
                            name='serviceType'
                            label={<Typography.Text className='w-[156px] ml-3 text-right text-[16px]/[22px] text-[#141414] tracking-normal opacity-100'>{createTicketMessages.serviceType}</Typography.Text>}
                        >
                            <Select
                                loading={isLoading || isFetching}
                                className='w-[552px] h-[40px]'
                                placeholder={createTicketMessages.serviceType}
                                options={[
                                    ...(serviceTypesOptions?.map((itm) =>({
                                        label: itm.name,
                                        value: itm.serviceTypeId
                                    })) || [])
                                ]}
                            >
                            </Select>
                        </FormItem>
                    )} */}

            {formFields?.appointmentDate && (
              <FormItem<CreateTicketFormType>
                name='appointmentDate'
                label={
                  <Typography.Text className='ml-3 w-[156px] text-right text-[16px]/[22px] tracking-normal text-[#141414] opacity-100'>
                    {createTicketMessages.appointmentDate}
                  </Typography.Text>
                }
              >
                <DatePicker locale={locale} placeholder={DATE_FORMAT} className='h-[40px] w-[272px]' />
              </FormItem>
            )}
            {formFields?.requestContent && (
              <FormItem<CreateTicketFormType>
                name='content'
                label={
                  <Typography.Text className='w-[156px] text-right text-[16px]/[22px] tracking-normal text-[#141414] opacity-100'>
                    {createTicketMessages.requestContent}
                  </Typography.Text>
                }
                rules={validationRules.content}
              >
                <Space className='mt-2 w-[552px] rounded-lg bg-white' direction='vertical'>
                  <Space className='flex min-h-10 flex-wrap gap-2 rounded-t-lg border-b-[1px] bg-white py-1'>
                    {serviceTypeId
                      ? ticketSampleActive?.map((sampleActive) => (
                          <>
                            <Tooltip key={sampleActive.ticketSampleId} title={sampleActive.summary}>
                              <Typography.Link
                                key={sampleActive.ticketSampleId}
                                onClick={() => handleTicketSampleClick(sampleActive.content)}
                                className='ml-6 text-nowrap text-left text-[14px]/[19px] tracking-normal text-[#1564E8] opacity-100'
                              >
                                {sampleActive.title}
                              </Typography.Link>
                            </Tooltip>
                          </>
                        ))
                      : []}
                  </Space>
                  <FormItem<CreateTicketFormType> className='' name='content'>
                    <Input.TextArea
                      ref={textareaRef}
                      value={content}
                      // onChange={handleHotkeyInput}
                      autoSize
                      className='w-[552px] border-0'
                    />
                    {isSuggestionVisible && (
                      <div className='absolute z-10 max-h-40 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-md'>
                        {suggestions.map((suggestion) => (
                          <div
                            key={suggestion}
                            className='cursor-pointer px-4 py-2 hover:bg-gray-100'
                            onClick={() => handleSuggestionSelect(suggestion)}
                          >
                            {suggestion}
                          </div>
                        ))}
                      </div>
                    )}
                  </FormItem>
                </Space>
              </FormItem>
            )}
            {formFields?.file && (
              <FormItem<CreateTicketFormType>
                valuePropName='fileList'
                name='uploadFileIds'
                getValueFromEvent={normFile}
              >
                {/* <Upload.Dragger className='w-[552px] ml-[182px]' multiple listType='picture-card' maxCount={5} /> */}
                <Upload.ServerDragger
                  className='ml-[182px] w-[552px]'
                  multiple
                  listType='picture-card'
                  maxCount={5}
                  fileList={fileList}
                  onChange={handleChange}
                  onPreview={handlePreview}
                  bucketName={FilesBucketNames.TICKET} // ✅ Đúng, vì ServerDragger nhận prop này
                />
              </FormItem>
            )}

            {formFields?.phaseNumber && (
              <FormItem
                name='phaseNumber'
                className=''
                label={
                  <Typography.Text className='w-[156px] text-right text-[16px]/[22px] tracking-normal text-[#141414] opacity-100'>
                    {createTicketMessages.phaseNumber}
                  </Typography.Text>
                }
              >
                <Select
                  className='h-[40px] w-[272px]'
                  placeholder={createTicketMessages.phaseNumber}
                  options={[
                    { value: 1, label: createTicketMessages.phaseNumber1 },
                    { value: 3, label: createTicketMessages.phaseNumber3 }
                  ]}
                ></Select>
              </FormItem>
            )}
            {formFields?.intendedUse && (
              <FormItem
                name='usagePurpose'
                label={
                  <Typography.Text className='w-[156px] text-right text-[16px]/[22px] tracking-normal text-[#141414] opacity-100'>
                    {createTicketMessages.usagePurpose}
                  </Typography.Text>
                }
              >
                <Select
                  className='h-[40px] w-[272px]'
                  placeholder={createTicketMessages.usagePurpose}
                  options={[
                    { value: 'sinhhoat', label: createTicketMessages.residential },
                    { value: 'ngoaisinhhoat', label: createTicketMessages.nonResidential }
                  ]}
                ></Select>
              </FormItem>
            )}
            {formFields?.outageAt && (
              <FormItem
                name='outageAt'
                label={
                  <Typography.Text className='ml-3 w-[156px] text-right text-[16px]/[22px] tracking-normal text-[#141414] opacity-100'>
                    {createTicketMessages.outageAt}
                  </Typography.Text>
                }
              >
                <DatePicker locale={locale} showTime className='h-[40px] w-[272px]' />
              </FormItem>
            )}
            {formFields?.incidentAddress && (
              <FormItem
                name='incidentAddress'
                label={
                  <Typography.Text className='ml-3 w-[156px] text-right text-[16px]/[22px] tracking-normal text-[#141414] opacity-100'>
                    {createTicketMessages.incidentAddress}
                  </Typography.Text>
                }
                rules={validationRules.incidentAddress}
              >
                <Input placeholder={createTicketMessages.incidentAddress} className='h-[40px] w-[552px]' />
              </FormItem>
            )}
            {formFields?.incidentArea && (
              <div className='ml-[182px] flex w-[552px] flex-row gap-2'>
                <FormItem name='incidentDistrict' style={{ display: 'none' }}>
                  <Input type='hidden' />
                </FormItem>
                <FormItem name='incidentWard'>
                  <Select
                    className='h-[40px] w-[272px]'
                    placeholder={createTicketMessages.selectWard}
                    options={[
                      ...(wards?.map((itm) => ({
                        label: itm.administrativeUnitName,
                        value: itm.administrativeUnitId
                      })) || [])
                    ]}
                    showSearch
                    optionFilterProp='children'
                    filterOption={(input, option) =>
                      removeVietnameseTones(option?.label ?? '')
                        .toLowerCase()
                        .includes(removeVietnameseTones(input).toLowerCase())
                    }
                    dropdownStyle={{ minWidth: '500px' }}
                    allowClear
                    notFoundContent={
                      <div className='flex flex-row items-center gap-2'>
                        <NoticeCircleIcon />
                        <Typography.Text type='danger' className='text-[14px]/[20px] font-normal'>
                          {customerLookupMessages.pleaseSelectDistrict}
                        </Typography.Text>
                      </div>
                    }
                  />
                </FormItem>
              </div>
            )}

            {formFields?.reason && (
              <FormItem name='outageReasonId'>
                <Select
                  className='ml-[182px] h-[40px] w-[272px]'
                  placeholder={createTicketMessages.selectOutageReason}
                  options={[
                    ...(optionsOutageReason.map((itm) => ({
                      label: itm.name,
                      value: itm.optionId
                    })) || [])
                  ]}
                />
              </FormItem>
            )}
            {formFields?.outageEntireArea && (
              <FormItem className='' name='isOutageEntireArea' valuePropName='checked'>
                <Checkbox className='ml-[182px]' onChange={onCheckbox}>
                  <Typography.Text className='text-left text-[16px]/[20px] tracking-normal text-[#868D97] opacity-100'>
                    {createTicketMessages.isOutageEntireArea}
                  </Typography.Text>
                </Checkbox>
              </FormItem>
            )}
            <div className='ml-[182px] flex flex-row gap-6'>
              {formFields?.notRequestToSendSms && (
                <FormItem className='' name='isNotRequestToSendSms' valuePropName='checked'>
                  <Checkbox onChange={onCheckbox}>
                    <Typography.Text className='text-left text-[16px]/[20px] tracking-normal text-[#868D97] opacity-100'>
                      {createTicketMessages.notMessageSendingRequest}
                    </Typography.Text>
                  </Checkbox>
                </FormItem>
              )}
              {formFields?.isCalledOut && (
                <FormItem className='' name='outgoingCall' valuePropName='checked'>
                  <Checkbox onChange={onCheckbox}>
                    <Typography.Text className='text-left text-[16px]/[20px] tracking-normal text-[#868D97] opacity-100'>
                      {createTicketMessages.outgoingCall}
                    </Typography.Text>
                  </Checkbox>
                </FormItem>
              )}
            </div>
            <Divider className='my-5 w-full' type='horizontal'></Divider>

            <div className='flex flex-row gap-6'>
              {isInfoFilled && (
                <div className={'w-full'}>
                  <div className={'mb-2 w-full'}>
                    <Typography.Text className='mb-6 text-left text-[16px]/[20px] font-semibold text-[#141414]'>
                      {createTicketMessages.wrongCompletedTicketList}
                    </Typography.Text>
                  </div>

                  <div>
                    {wrongTicketData?.data?.rows?.length
                      ? wrongTicketData.data.rows.map((item) => (
                          <div
                            onClick={() => openDetailTicket(item.ticketId)}
                            className={
                              'mt-2 flex w-full cursor-pointer items-center justify-between space-x-4 rounded-lg bg-black/10 p-4 hover:bg-black/15'
                            }
                          >
                            <Checkbox
                              key={item?.ticketId}
                              checked={item?.isRequestMultipleTime || selectedIds.includes(item.ticketId)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleCheckboxChange(item.ticketId, e.target.checked);
                              }}
                              className={'w-1/5'}
                            >
                              <div>
                                <Typography.Text>{item?.ticketId}</Typography.Text>
                              </div>
                            </Checkbox>

                            <div className={'w-2/5'}>
                              <Typography.Text className={'font-semibold'}>Ngày tạo:</Typography.Text>{' '}
                              {item?.createdAt ? dayjs(item.createdAt).format('DD/MM/YYYY HH:mm:ss') : ''}
                            </div>
                            <div className={'w-2/5'}>
                              <Typography.Text className={'font-semibold'}>Ngày đóng:</Typography.Text>{' '}
                              {item?.closedAt ? dayjs(item.closedAt).format('DD/MM/YYYY HH:mm:ss') : ''}
                            </div>
                          </div>
                        ))
                      : !isFetching && <Typography.Text>Không có phiếu đóng sai quy định</Typography.Text>}

                    <Modal
                      centered
                      className='ant-modal-body-p-none h-[95vh] w-fit rounded-xl bg-[#F5F4F6]'
                      open={modalDetailTickets}
                      onCancel={closeDetailTicket}
                      footer={null}
                      destroyOnClose
                    >
                      <RequestDetail ticketId={detailTicket} cancelDetailModal={closeDetailTicket} />
                    </Modal>
                  </div>
                  <Divider className='my-5 w-full' type='horizontal'></Divider>
                </div>
              )}
            </div>

            <FormItem>
              <div className='mb-3 flex flex-row justify-center gap-6'>
                {serviceType?.data?.parent?.serviceTypeId !== 147 && (
                  <Button onClick={() => showReviewModal('orgUnit')} className='' type='primary' danger>
                    <Typography.Text className='text-left text-[16px]/[20px] font-semibold tracking-normal text-white opacity-100'>
                      {createTicketMessages.forwardForProcessing}
                    </Typography.Text>
                  </Button>
                )}
                <Button onClick={() => showReviewModal('supervisor')} className='' type='primary'>
                  <Typography.Text className='text-left text-[16px]/[20px] font-semibold tracking-normal text-white opacity-100'>
                    {createTicketMessages.redirectToTheCenter}
                  </Typography.Text>
                </Button>
                <Button onClick={() => showReviewModal('finish')} className='bg-green-700' type='primary'>
                  <Typography.Text className='text-left text-[16px]/[20px] font-semibold tracking-normal text-white opacity-100'>
                    Hoàn tất tại trung tâm
                  </Typography.Text>
                </Button>
              </div>
            </FormItem>
          </Spin>
        </Form>
        <Modal
          title={null} // Ẩn tiêu đề mặc định để custom header
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null} // Ẩn footer mặc định để tự thiết kế
          width={700} // Tùy chỉnh độ rộng modal
          closable={false} // Ẩn nút đóng mặc định
          className='ant-modal-body-p-none'
        >
          {/* Custom Header */}
          <div className='flex items-center justify-between border-b px-6 py-2'>
            {/* Nút đóng (X) chuyển qua bên trái */}
            <Button
              shape='circle'
              icon={<CloseIcon className='' />}
              onClick={() => setIsModalVisible(false)}
              className='border-[#F5F5F7] bg-[#F5F5F7] bg-transparent bg-clip-padding bg-no-repeat opacity-100 hover:bg-[#EBEBED]'
            />
            <Typography.Text className='flex-1 text-center text-xl font-semibold text-[#141414]'>
              {createTicketMessages.confirmSendTicket}
            </Typography.Text>
            {/* Nút chỉnh sửa bên phải */}
            <Button
              type='text'
              onClick={() => setIsModalVisible(false)} // Đóng modal để chỉnh sửa
            >
              <Typography.Text className='text-right text-[16px]/[22px] font-normal text-[#1564E8]'>
                {createTicketMessages.edit}
              </Typography.Text>
            </Button>
          </div>

          {/* Nội dung phiếu review */}
          <div className='px-6 py-6'>
            {reviewData && (
              <>
                <Typography.Text className='text-left text-[32px]/[40px] font-semibold text-[#141414]'>
                  {serviceType?.data.name}
                </Typography.Text>
                <div className='mt-8 flex h-[64px] w-full items-center justify-between rounded-[5px] bg-[#EBEBED]'>
                  <div className='flex flex-row items-center gap-4 pl-4'>
                    <Typography.Text className='text-left text-[16px]/[16px] font-normal text-[#6C737E]'>
                      {createTicketMessages.customerCode}
                    </Typography.Text>
                    <Typography.Text className='text-left text-[16px]/[20px] font-semibold text-[#141414]'>
                      {reviewData?.customerCode}
                    </Typography.Text>
                  </div>
                  <Image src={logoEVN} alt='logoEVN' className='mr-4 h-8 w-8' />
                </div>
                <div className='mt-5'>
                  <Typography.Text className='text-left text-[18px]/[22px] font-semibold text-[#141414]'>
                    {createTicketMessages.requesterInformation}
                  </Typography.Text>
                </div>
                {isDirectCustomerContact === true ? (
                  <div className='mt-1 flex flex-row gap-2'>
                    <AttentionRedCircleIcon className='h-4 w-4' />
                    <Typography.Text className='text-left text-xs font-normal italic text-[#D1131D]'>
                      {createTicketMessages.directCustomerContact}
                    </Typography.Text>
                  </div>
                ) : (
                  <></>
                )}
                <div className='mt-6 flex flex-row gap-4'>
                  <div className='w-[140px]'>
                    <Typography.Text className='text-base font-normal text-[#868D97]'>
                      {createTicketMessages.fullName}
                    </Typography.Text>
                  </div>
                  <div className='flex w-[464px] flex-col gap-1'>
                    <Tooltip title={reviewData?.customerRequestName}>
                      <Typography.Text className='truncate text-left text-base font-normal text-[#141414]'>
                        {reviewData?.customerRequestName}
                      </Typography.Text>
                    </Tooltip>
                  </div>
                </div>
                <div className='mt-6 flex flex-row gap-4'>
                  <div className='w-[140px]'>
                    <Typography.Text className='text-base font-normal text-[#868D97]'>
                      {createTicketMessages.phoneNumber}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text className='text-left text-base font-normal text-[#141414]'>
                      {reviewData?.customerRequestPhoneNumber}
                    </Typography.Text>
                  </div>
                </div>
                <div className='mt-6 flex flex-row gap-4'>
                  <div className='w-[140px]'>
                    <Typography.Text className='text-base font-normal text-[#868D97]'>
                      {createTicketMessages.address}
                    </Typography.Text>
                  </div>
                  <div className='flex w-[464px] flex-col gap-1'>
                    {isDirectCustomerContact === true ? (
                      <Tooltip title={reviewData?.customerRequestAddress}>
                        <Typography.Text className='truncate text-left text-base font-normal text-[#141414]'>
                          {reviewData?.customerRequestAddress}
                        </Typography.Text>
                      </Tooltip>
                    ) : (
                      <Tooltip title={reviewData?.customerRequestAddress}>
                        <Typography.Text className='text-left text-base font-normal text-[#141414]'>
                          {reviewData?.customerRequestAddress}
                        </Typography.Text>
                      </Tooltip>
                    )}
                    {reviewData.isAddressChange === true ? (
                      <div className='mt-1 flex flex-row gap-2'>
                        <AttentionRedCircleIcon className='h-4 w-4' />
                        <Typography.Text className='text-left text-xs font-normal italic text-[#D1131D]'>
                          {createTicketMessages.newAddress}
                        </Typography.Text>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
                <div className='mt-6 flex flex-row gap-4'>
                  <div className='w-[140px]'>
                    <Typography.Text className='text-base font-normal text-[#868D97]'>
                      {createTicketMessages.wardDistrict}
                    </Typography.Text>
                  </div>
                  <div className='flex w-[464px] flex-col gap-1'>
                    <Typography.Text className='text-left text-base font-normal text-[#141414]'>
                      {reviewData?.wardLabel}
                    </Typography.Text>
                  </div>
                </div>
                <div className='mt-6 flex flex-row gap-4'>
                  <div className='w-[140px]'>
                    <Typography.Text className='text-base font-normal text-[#868D97]'>
                      {createTicketMessages.receptionChannel}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text className='text-left text-base font-normal text-[#141414]'>
                      {reviewData?.channelLabel}
                    </Typography.Text>
                  </div>
                </div>
                <Divider></Divider>
                <div className='mt-4 flex flex-row gap-4'>
                  <div className='w-[140px]'>
                    <Typography.Text className='text-base font-normal text-[#868D97]'>
                      {createTicketMessages.citizenIdNumber}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text className='text-left text-base font-normal text-[#141414]'>
                      {reviewData?.customerRequestCicNumber}
                    </Typography.Text>
                  </div>
                </div>
                <div className='mt-4 flex flex-row gap-4'>
                  <div className='w-[140px]'>
                    <Typography.Text className='text-base font-normal text-[#868D97]'>
                      {createTicketMessages.issueDate}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text className='text-left text-base font-normal text-[#141414]'>
                      {reviewData?.customerRequestCicIssueDate
                        ? dayjs(reviewData?.customerRequestCicIssueDate).format('DD/MM/YYYY')
                        : null}
                    </Typography.Text>
                  </div>
                </div>
                <div className='mt-4 flex flex-row gap-4'>
                  <div className='w-[140px]'>
                    <Typography.Text className='text-base font-normal text-[#868D97]'>
                      {createTicketMessages.placeOfIssue}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text className='text-left text-base font-normal text-[#141414]'>
                      {reviewData?.customerRequestCicIssuePlace}
                    </Typography.Text>
                  </div>
                </div>
                <div className='mt-5 flex flex-row gap-2'>
                  <AttentionRedCircleIcon className='h-4 w-4' />
                  <Typography.Text className='text-left text-xs font-normal text-[#D1131D]'>
                    {optionsCicType?.find((opt) => opt.optionId === reviewData?.customerRequestCicType)?.name}
                  </Typography.Text>
                </div>
                <Divider></Divider>
                <div className='mt-5'>
                  <Typography.Text className='text-left text-[18px]/[22px] font-semibold text-[#141414]'>
                    {createTicketMessages.requestInformation}
                  </Typography.Text>
                </div>
                <div className='mt-8 flex h-[64px] w-full items-center justify-between rounded-[5px] bg-[#D1131D1F]'>
                  <div className='flex flex-row items-center gap-4 pl-4'>
                    <Typography.Text className='text-left text-[16px]/[16px] font-normal text-[#6C737E]'>
                      {createTicketMessages.appointmentDate}
                    </Typography.Text>
                    <Typography.Text className='text-left text-[16px]/[20px] font-semibold text-[#141414]'>
                      {dayjs.utc(reviewData?.appointmentDate).tz('Asia/Ho_Chi_Minh').format('DD/MM/YYYY')}
                    </Typography.Text>
                  </div>
                  <CalendarRedIcon className='mr-4 h-8 w-8' />
                </div>
                <div className='mt-5'>
                  <Typography.Paragraph className='text-left text-[16px]/[22px] font-normal text-[#141414]'>
                    {reviewData?.content}
                  </Typography.Paragraph>
                </div>
                <Divider></Divider>
                {/* <Typography.Text className='font-normal text-[16px]/[22px] text-[#141414] text-left'>{createTicketMessages.attachFile}</Typography.Text> */}
                {/* <TicketIDetailedFileAttachment direction='vertical' files={reviewData?.uploadFileIds?.flatMap(file => Number(file?.uid)) || []} ></TicketIDetailedFileAttachment> */}
                <Typography.Text className='text-base font-normal text-[#868D97]'>
                  {createTicketMessages.attachFile}:
                </Typography.Text>
                <div className='file-preview-container mt-5' style={{ display: 'flex' }}>
                  {fileList.map((file) => (
                    <div key={file.uid} className='file-preview-item' style={{ textAlign: 'center' }}>
                      {renderFilePreview(file)}
                    </div>
                  ))}
                </div>

                {(selectedIds.length > 0 || autoCheckedIds.length > 0) && (
                  <>
                    <Divider></Divider>
                    <Typography.Text className='text-base font-normal text-[#868D97]'>
                      {createTicketMessages.wrongTicketList}:
                    </Typography.Text>
                    <div className='file-preview-container mt-5' style={{ display: 'flex' }}>
                      {Array.from(new Set([...autoCheckedIds, ...selectedIds])).map((file, index, arr) => (
                        <div key={file} className='file-preview-item' style={{ textAlign: 'center' }}>
                          {file}
                          {index !== arr.length - 1 && ' , '}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <div className='flex flex-col gap-5'>
                  {reviewData.isNotRequestToSendSms === true ? (
                    <div className='mt-1 flex flex-row gap-2'>
                      <AttentionRedCircleIcon className='h-4 w-4' />
                      <Typography.Text className='text-left text-xs font-normal text-[#D1131D]'>
                        {createTicketMessages.notMessageSendingRequest}
                      </Typography.Text>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer Custom với nút xác nhận ở giữa */}
          <div className='flex justify-center border-t px-6 py-4'>
            {submitType === 'orgUnit' && (
              <Button
                type='primary'
                className='px-6'
                onClick={() => {
                  form.submit(); // Gửi phiếu khi xác nhận
                  setIsModalVisible(false);
                }}
              >
                {createTicketMessages.transferProcessingUnit} {/* Nút cho orgUnit */}
              </Button>
            )}
            {submitType === 'supervisor' && (
              <Button
                type='primary'
                danger
                className='px-6'
                onClick={() => {
                  form.submit(); // Gửi phiếu khi xác nhận
                  setIsModalVisible(false);
                }}
              >
                {createTicketMessages.redirectToTheCenter} {/* Nút cho supervisor */}
              </Button>
            )}
            {submitType === 'finish' && (
              <Button
                type='primary'
                danger
                className='px-6'
                onClick={() => {
                  form.submit(); // Gửi phiếu khi xác nhận
                  setIsModalVisible(false);
                }}
              >
                Hoàn tất tại trung tâm {/* Nút cho supervisor */}
              </Button>
            )}
          </div>
        </Modal>
        <Modal
          className='ant-modal-body-p-none max-h-[1000px] w-[1088px]'
          open={isModalOpen}
          onCancel={handleCancel}
          footer={null}
          destroyOnClose
        >
          <TicketDetailed
            currentSessionId={currentSessionId}
            status={ticketDetail?.status.code}
            ticketId={selectedTicketId || 0}
            cancelDetailModal={handleCancel}
            isSecondCall={true}
          />
        </Modal>
      </div>
    );
  }
);

export default CreateTicketForm;
